import React, { useCallback, useEffect, useMemo, useState } from 'react';

import {
  Box,
  Button,
  Chip,
  Container,
  FormControl,
  FormControlLabel,
  FormHelperText,
  InputLabel,
  MenuItem,
  Radio,
  RadioGroup,
  Select,
  TextField,
  Tooltip,
  Typography,
} from '@material-ui/core';
import clsx from 'clsx';
import { format } from 'date-fns';
import { useSnackbar } from 'notistack';
import { useTranslation } from 'react-i18next';
import { useHistory, useLocation, useParams } from 'react-router-dom';

import ColoredButton from 'components/ColoredButton';
import ErrorBoundary from 'components/ErrorBoundary';
import Loader from 'components/Loader';
import ManagePageHeader from 'components/ManagePageHeader/ManagePageHeader';
import TextButton from 'components/TextButton/TextButton';
import { DPIA_DECISION_OPTIONS } from 'config/api/dpias/dpias.constants';
import { calculateMockRiskLevel } from 'config/api/dpias/dpias.helpers';
import type { DpiaDecision } from 'config/api/dpias/dpias.types';
import usePageTitle from 'hooks/usePageTitle';
import general_messages from 'messages/general_messages';
import processing_activities_messages from 'messages/processing_activities_messages';
import validation_messages from 'messages/validation_messages';
import PATHS from 'router/PATHS';
import userDataStorage from 'storages/userDataStorage';

import { getDpiaDecisionLabel, getDpiaDecisionTooltip } from '../_shared/apaDpiaStatus.helpers';
import { canAccessAdminDpiaReviewPage } from '../_shared/assetRopaReviewAccess';
import { formatDpiaSidePanelList, formatDpiaSidePanelText } from '../_shared/dpiaSidePanelDisplay.helpers';
import QueryUnavailableState from '../_shared/QueryUnavailableState';
import useStyles from '../ProcessingActivitiesAssetReviewsPage/ProcessingActivitiesAssetReviewsPage.styles';

import {
  buildBlockingApprovalErrors,
  buildReviewSummary,
  buildReviewWarnings,
  canMakeReviewDecision,
  getClarificationRequestMeta,
  hasClarificationHistory,
  isChangedAfterSubmission,
} from './adminDpiaReview.helpers';
import useAdminDpiaReview from './useAdminDpiaReview';

type RouteParams = {
  dpiaId: string;
};

type LocationState = {
  returnPath?: string;
};

const formatTimestamp = (value: string | null) => {
  if (!value) return '—';

  try {
    return format(new Date(value), 'yyyy-MM-dd HH:mm');
  } catch {
    return value;
  }
};

type AdminDpiaReviewSectionProps = {
  children: React.ReactNode;
  className?: string;
  contentClassName?: string;
  eyebrow: string;
  help?: string;
  title: string;
};

const AdminDpiaReviewSection: React.FC<AdminDpiaReviewSectionProps> = ({ children, className, contentClassName, eyebrow, help, title }) => {
  const styles = useStyles();

  return (
    <Box className={clsx(styles.apaSection, className)}>
      <div className={styles.apaSectionHeader}>
        <div className={styles.reviewEyebrow}>{eyebrow}</div>
        <Typography className={styles.apaSectionTitle} variant='h3'>
          {title}
        </Typography>
        {help && (
          <Typography className={styles.apaSectionHelp} variant='body2'>
            {help}
          </Typography>
        )}
      </div>
      <div className={clsx(styles.apaSectionContent, contentClassName)}>{children}</div>
    </Box>
  );
};

const AdminDpiaReviewPage: React.FC = () => {
  const { t } = useTranslation();
  const styles = useStyles();
  const history = useHistory();
  const location = useLocation<LocationState>();
  const { dpiaId } = useParams<RouteParams>();
  const { enqueueSnackbar } = useSnackbar();
  const userData = userDataStorage.get() || {};
  const organizationId = typeof userData.organization === 'number' ? userData.organization : undefined;
  const canAccess = canAccessAdminDpiaReviewPage(userData);
  const ownerMessages = processing_activities_messages.asset_ropa.owner_dpia;
  const messages = processing_activities_messages.asset_ropa.admin_dpia_review;
  const [expandedRiskIds, setExpandedRiskIds] = useState<number[]>([]);
  const [dpiaDecision, setDpiaDecision] = useState<DpiaDecision | null>(null);

  usePageTitle(t(messages.page_title));

  const returnPath = location.state?.returnPath ?? PATHS.PROCESSING_ACTIVITIES_ASSET_REVIEWS;

  const navigateBack = useCallback(() => {
    history.push(returnPath);
  }, [history, returnPath]);

  const validationMessages = useMemo(
    () => ({
      required: t(validation_messages.required),
      purposeScope: t(ownerMessages.validation.purpose_scope),
      riskTitle: t(ownerMessages.validation.risk_title),
      riskDescription: t(ownerMessages.validation.risk_description),
      riskAffectedRights: t(ownerMessages.validation.risk_affected_rights),
      riskLikelihood: t(ownerMessages.validation.risk_likelihood),
      riskImpact: t(ownerMessages.validation.risk_impact),
      riskMeasures: t(ownerMessages.validation.risk_measures),
      measureTitle: t(ownerMessages.validation.measure_title),
      measureDescription: t(ownerMessages.validation.measure_description),
      measureResponsible: t(ownerMessages.validation.measure_responsible),
      measureStatus: t(ownerMessages.validation.measure_status),
      clarificationResponse: t(ownerMessages.validation.clarification_response),
      residualRisk: t(ownerMessages.validation.residual_risk),
      priorConsultation: t(ownerMessages.validation.prior_consultation),
      changedAfterSubmission: t(messages.validation.changed_after_submission),
      incompleteApproval: t(messages.validation.incomplete_approval),
      clarificationComment: t(messages.validation.clarification_comment),
      reviewCommentRequired: t(messages.validation.review_comment_required),
      decisionChoiceRequired: t(messages.validation.decision_choice_required),
    }),
    [messages, ownerMessages, t],
  );

  const warningMessages = useMemo(
    () => ({
      changedAfterSubmission: t(messages.validation.changed_after_submission),
      highResidualRisk: t(messages.warnings.high_residual_risk),
      priorConsultationMayBeRequired: t(messages.warnings.prior_consultation_may_be_required),
      priorConsultationRequired: t(messages.warnings.prior_consultation_required),
      openMeasures: t(messages.warnings.open_measures),
      thirdCountryProcessing: t(messages.warnings.third_country_processing),
    }),
    [messages, t],
  );

  const {
    bannerMeta,
    context,
    detail,
    formValues,
    handleApprove,
    handleRequestClarification,
    isApproving,
    isError,
    isLoading,
    isRequestingClarification,
    isUnavailable,
    unavailableStatus,
    refetch,
    decisionComment,
    setDecisionComment,
    clarificationComment,
    setClarificationComment,
    setValidationErrors,
    validationErrors,
  } = useAdminDpiaReview({
    dpiaId: Number(dpiaId),
    onApproveSuccess: () => {
      enqueueSnackbar(t(messages.toasts.decision_submitted), { variant: 'success' });
      navigateBack();
    },
    onClarificationSuccess: () => {
      enqueueSnackbar(t(messages.toasts.clarification_requested), { variant: 'success' });
      navigateBack();
    },
    organizationId,
  });

  const reviewSummary = formValues ? buildReviewSummary(formValues) : null;
  const reviewWarnings = detail && context && formValues ? buildReviewWarnings(detail, context, formValues, warningMessages) : [];
  const blockingErrors = detail && formValues ? buildBlockingApprovalErrors(detail, formValues, validationMessages) : [];
  const canDecide = detail ? canMakeReviewDecision(detail.status) : false;
  const changedAfterSubmission = detail ? isChangedAfterSubmission(detail) : false;
  const showClarificationHistory = detail ? hasClarificationHistory(detail) : false;

  useEffect(() => {
    if (!formValues?.risks.length) {
      setExpandedRiskIds([]);
      return;
    }

    setExpandedRiskIds(current => {
      const validRiskIds = new Set(formValues.risks.map(risk => risk.id));
      const preservedExpandedIds = current.filter(id => validRiskIds.has(id));

      if (preservedExpandedIds.length > 0) {
        return preservedExpandedIds;
      }

      return [formValues.risks[0].id];
    });
  }, [formValues?.risks]);

  useEffect(() => {
    setDpiaDecision(null);
  }, [detail?.id, detail?.revision, detail?.status]);

  const isSubmittingDecision = isApproving || isRequestingClarification;
  const isApproveDecisionBlocked = changedAfterSubmission || blockingErrors.length > 0;
  const canShowSubmitDecision = Boolean(dpiaDecision && decisionComment.trim());
  const isSubmitDecisionDisabled = !canShowSubmitDecision || isSubmittingDecision || isApproveDecisionBlocked;

  const handleSubmitDecision = useCallback(async () => {
    if (!dpiaDecision) {
      return;
    }

    try {
      await handleApprove(dpiaDecision, validationMessages);
    } catch {
      if (validationErrors.approval || validationErrors.reviewComment || validationErrors.decision) {
        return;
      }

      enqueueSnackbar(t(processing_activities_messages.asset_ropa.toasts.action_failed), { variant: 'error' });
    }
  }, [
    dpiaDecision,
    enqueueSnackbar,
    handleApprove,
    t,
    validationErrors.approval,
    validationErrors.decision,
    validationErrors.reviewComment,
    validationMessages,
  ]);

  const handleSubmitClarification = useCallback(async () => {
    try {
      await handleRequestClarification(validationMessages);
    } catch {
      if (validationErrors.clarificationComment) {
        return;
      }

      enqueueSnackbar(t(processing_activities_messages.asset_ropa.toasts.action_failed), { variant: 'error' });
    }
  }, [enqueueSnackbar, handleRequestClarification, t, validationErrors.clarificationComment, validationMessages]);

  let content: React.ReactNode;

  if (!canAccess) {
    content = (
      <Typography color='textSecondary' variant='body2'>
        {t(processing_activities_messages.asset_ropa.forbidden_body)}
      </Typography>
    );
  } else if (isUnavailable) {
    content = (
      <QueryUnavailableState
        backLabel={t(general_messages.back)}
        message={unavailableStatus === 403 ? t(processing_activities_messages.asset_ropa.forbidden_body) : t(messages.errors.not_found)}
        onBack={navigateBack}
      />
    );
  } else if (isError) {
    content = (
      <Box className={styles.errorBanner}>
        <Typography color='error' variant='body2'>
          {t(processing_activities_messages.asset_ropa.errors.load_failed)}
        </Typography>
        <TextButton onClick={() => refetch()}>{t(general_messages.retry)}</TextButton>
      </Box>
    );
  } else if (isLoading || !formValues || !detail || !context || !bannerMeta) {
    content = <Loader inner />;
  } else {
    const clarificationRequestMeta = getClarificationRequestMeta(detail);

    content = (
      <>
        <Box className={styles.reviewPanel}>
          <div className={styles.reviewMeta}>
            <span>
              <strong>{t(messages.status)}:</strong>{' '}
              <span className={`${styles.statusBadge} ${styles.statusBadgeInfo}`}>{t(ownerMessages.statuses[detail.status])}</span>
            </span>
            <span>
              <strong>{t(messages.responsible_owner)}:</strong> {formatDpiaSidePanelText(bannerMeta.responsibleOwner)}
            </span>
            <span>
              <strong>{t(messages.submitted)}:</strong> {formatTimestamp(detail.submittedAt)}
            </span>
            <span>
              <strong>{t(messages.submitted_revision)}:</strong> {detail.submittedRevision ?? '—'}
            </span>
            <span>
              <strong>{t(messages.current_revision)}:</strong> {detail.revision}
            </span>
          </div>
        </Box>

        <Box className={`${styles.reviewPanel} ${styles.adminDpiaReviewDecisionSection}`} style={{ marginTop: 0 }}>
          <Typography variant='body2'>
            <strong>{t(messages.integrity_banner.title)}</strong> {t(messages.integrity_banner.body)}
          </Typography>
          {changedAfterSubmission && (
            <Typography color='error' style={{ marginTop: 8 }} variant='body2'>
              {t(messages.changed_after_submission_badge)}
            </Typography>
          )}
        </Box>

        <div className={styles.twoColumnLayout}>
          <aside>
            <Box className={styles.sidePanel}>
              <div className={styles.reviewEyebrow}>{t(ownerMessages.side_panel.eyebrow)}</div>
              <Typography gutterBottom variant='h3'>
                {t(ownerMessages.side_panel.title)}
              </Typography>
              <div className={styles.fieldList}>
                <div>
                  <div className={styles.fieldLabel}>{t(ownerMessages.side_panel.asset)}</div>
                  <div>{formatDpiaSidePanelText(context.assetName)}</div>
                </div>
                <div>
                  <div className={styles.fieldLabel}>{t(ownerMessages.side_panel.processing_activity)}</div>
                  <div>{formatDpiaSidePanelText(context.assetProcessingActivityName)}</div>
                </div>
                <div>
                  <div className={styles.fieldLabel}>{t(ownerMessages.side_panel.asset_type)}</div>
                  <div>{formatDpiaSidePanelText(context.assetTypeName)}</div>
                </div>
                <div>
                  <div className={styles.fieldLabel}>{t(ownerMessages.side_panel.owner)}</div>
                  <div>{formatDpiaSidePanelText(context.operationalOwnerName)}</div>
                </div>
                <div>
                  <div className={styles.fieldLabel}>{t(ownerMessages.side_panel.data_subjects)}</div>
                  <div>{formatDpiaSidePanelList(context.dataSubjectCategories)}</div>
                </div>
                <div>
                  <div className={styles.fieldLabel}>{t(ownerMessages.side_panel.personal_data)}</div>
                  <div>{formatDpiaSidePanelList(context.personalDataCategories)}</div>
                </div>
                <div>
                  <div className={styles.fieldLabel}>{t(ownerMessages.side_panel.countries)}</div>
                  <div>{formatDpiaSidePanelList(context.countryHandling)}</div>
                </div>
                <div>
                  <div className={styles.fieldLabel}>{t(ownerMessages.side_panel.minimum_security)}</div>
                  <div>{formatDpiaSidePanelText(context.minimumSecurityAssessment)}</div>
                </div>
              </div>

              <Typography gutterBottom style={{ marginTop: 16 }} variant='h4'>
                {t(ownerMessages.side_panel.purpose_scope)}
              </Typography>
              {context.availablePurposes.map(purpose => (
                <Box key={purpose.id} className={styles.purposePanel}>
                  <Typography variant='body2'>
                    <strong>{purpose.name}</strong>
                  </Typography>
                  <Typography color='textSecondary' variant='caption'>
                    {purpose.legalBasis}
                  </Typography>
                  <Typography variant='body2'>{purpose.applicationDescription}</Typography>
                </Box>
              ))}

              <Typography gutterBottom style={{ marginTop: 16 }} variant='h4'>
                {t(ownerMessages.side_panel.source_request)}
              </Typography>
              <Typography variant='body2'>
                <strong>{t(ownerMessages.side_panel.recommendation)}:</strong>{' '}
                {formatDpiaSidePanelText(context.sourceRequest.recommendation)}
              </Typography>
              <Typography variant='body2'>
                <strong>{t(ownerMessages.side_panel.decision)}:</strong> {formatDpiaSidePanelText(context.sourceRequest.decision)}
              </Typography>
              <Typography color='textSecondary' variant='body2'>
                {formatDpiaSidePanelText(context.sourceRequest.decisionComment)}
              </Typography>
            </Box>
          </aside>

          <section className={styles.mainPanel}>
            <AdminDpiaReviewSection
              contentClassName={styles.apaSectionFields}
              eyebrow='SECTION 1'
              title={t(ownerMessages.sections.scope.title)}
            >
              {(
                [
                  ['plannedProcessingDescription', ownerMessages.sections.scope.planned_processing],
                  ['assessmentScope', ownerMessages.sections.scope.assessment_scope],
                  ['projectOrChangeContext', ownerMessages.sections.scope.project_context],
                ] as const
              ).map(([field, label]) => (
                <TextField
                  key={field}
                  disabled
                  fullWidth
                  label={t(label)}
                  margin='none'
                  multiline
                  rows={field === 'plannedProcessingDescription' ? 4 : 3}
                  value={formValues[field]}
                  variant='outlined'
                />
              ))}
              <Typography variant='body2'>{t(ownerMessages.sections.scope.purposes)}</Typography>
              <div className={styles.reviewActions}>
                {context.availablePurposes
                  .filter(purpose => formValues.selectedPurposeIds.includes(purpose.id))
                  .map(purpose => (
                    <Chip key={purpose.id} color='primary' label={purpose.name} />
                  ))}
              </div>
            </AdminDpiaReviewSection>

            <AdminDpiaReviewSection
              contentClassName={styles.apaSectionFields}
              eyebrow='SECTION 2'
              title={t(ownerMessages.sections.necessity.title)}
            >
              {(
                ['necessityAssessment', 'proportionalityAssessment', 'dataMinimisationAssessment', 'retentionAndAccessAssessment'] as const
              ).map(field => (
                <TextField
                  key={field}
                  disabled
                  fullWidth
                  label={t(ownerMessages.sections.necessity[field])}
                  margin='none'
                  multiline
                  rows={3}
                  value={formValues[field]}
                  variant='outlined'
                />
              ))}
            </AdminDpiaReviewSection>

            <AdminDpiaReviewSection
              eyebrow='SECTION 3'
              help={t(ownerMessages.sections.risks.help)}
              title={t(ownerMessages.sections.risks.title)}
            >
              {formValues.risks.map(risk => {
                const expanded = expandedRiskIds.includes(risk.id);
                const riskLevel =
                  calculateMockRiskLevel(risk.likelihood, risk.impact) || risk.riskLevel || t(ownerMessages.sections.risks.not_assessed);

                return (
                  <Box key={risk.id} className={styles.purposePanel}>
                    <div className={styles.riskPanelHeader}>
                      <div className={styles.riskPanelTitle}>
                        <Typography variant='body1'>
                          <strong>{risk.title || t(ownerMessages.sections.risks.untitled)}</strong>
                        </Typography>
                        <Typography color='textSecondary' variant='caption'>
                          {t(ownerMessages.sections.risks.likelihood)}:{' '}
                          {risk.likelihood ? t(ownerMessages.likelihood[risk.likelihood]) : '—'} · {t(ownerMessages.sections.risks.impact)}:{' '}
                          {risk.impact ? t(ownerMessages.impact[risk.impact]) : '—'} · {riskLevel}
                        </Typography>
                      </div>
                      <Button
                        onClick={() =>
                          setExpandedRiskIds(current => (expanded ? current.filter(id => id !== risk.id) : [...current, risk.id]))
                        }
                        type='button'
                      >
                        {expanded ? t(ownerMessages.sections.risks.collapse) : t(ownerMessages.sections.risks.expand)}
                      </Button>
                    </div>
                    {expanded && (
                      <div className={styles.purposePanelBody}>
                        <TextField
                          disabled
                          fullWidth
                          label={t(ownerMessages.sections.risks.risk_description)}
                          margin='normal'
                          multiline
                          rows={3}
                          value={risk.description}
                          variant='outlined'
                        />
                        <TextField
                          disabled
                          fullWidth
                          label={t(ownerMessages.sections.risks.affected_rights)}
                          margin='normal'
                          multiline
                          rows={3}
                          value={risk.affectedRightsAndFreedoms}
                          variant='outlined'
                        />
                        <Typography gutterBottom variant='h4'>
                          {t(ownerMessages.sections.risks.measures)}
                        </Typography>
                        {risk.measures.map(measure => {
                          const responsibleLabel =
                            measure.responsibleName.trim() || (measure.responsibleMembershipId > 0 ? detail.responsibleName.trim() : '');

                          return (
                            <Box key={measure.id} className={styles.purposePanel}>
                              <div className={styles.reviewPanelHeader}>
                                <div>
                                  <Typography variant='body2'>
                                    <strong>{measure.title}</strong>
                                  </Typography>
                                  <Typography color='textSecondary' variant='caption'>
                                    {measure.description}
                                  </Typography>
                                </div>
                                <div className={styles.reviewActions}>
                                  <Chip label={t(ownerMessages.measure_status[measure.status])} size='small' />
                                  {responsibleLabel && <Chip label={responsibleLabel} size='small' variant='outlined' />}
                                </div>
                              </div>
                            </Box>
                          );
                        })}
                      </div>
                    )}
                  </Box>
                );
              })}
            </AdminDpiaReviewSection>

            <AdminDpiaReviewSection
              contentClassName={styles.apaSectionFields}
              eyebrow='SECTION 4'
              title={t(ownerMessages.sections.overall.title)}
            >
              <TextField
                disabled
                fullWidth
                label={t(ownerMessages.sections.overall.overall_assessment)}
                margin='none'
                multiline
                rows={4}
                value={formValues.overallAssessment}
                variant='outlined'
              />
              <div className={styles.filtersRow}>
                <FormControl disabled fullWidth margin='none' variant='outlined'>
                  <InputLabel>{t(ownerMessages.sections.overall.residual_risk)}</InputLabel>
                  <Select label={t(ownerMessages.sections.overall.residual_risk)} value={formValues.residualRiskLevel}>
                    {Object.keys(ownerMessages.residual_risk).map(option => (
                      <MenuItem key={option} value={option}>
                        {t(ownerMessages.residual_risk[option as keyof typeof ownerMessages.residual_risk])}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
                <FormControl disabled fullWidth margin='none' variant='outlined'>
                  <InputLabel>{t(ownerMessages.sections.overall.prior_consultation)}</InputLabel>
                  <Select label={t(ownerMessages.sections.overall.prior_consultation)} value={formValues.priorConsultationAssessment}>
                    {Object.keys(ownerMessages.prior_consultation).map(option => (
                      <MenuItem key={option} value={option}>
                        {t(ownerMessages.prior_consultation[option as keyof typeof ownerMessages.prior_consultation])}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </div>
              <TextField
                disabled
                fullWidth
                label={t(ownerMessages.sections.overall.owner_conclusion)}
                margin='none'
                multiline
                rows={3}
                value={formValues.ownerConclusion}
                variant='outlined'
              />
            </AdminDpiaReviewSection>

            {(showClarificationHistory || reviewSummary) && (
              <Box className={styles.adminDpiaReviewSupportZone}>
                {showClarificationHistory && (
                  <AdminDpiaReviewSection
                    contentClassName={styles.apaSectionFields}
                    eyebrow={t(messages.sections.clarification_history.eyebrow)}
                    title={t(messages.sections.clarification_history.title)}
                  >
                    {detail.administratorClarificationComment && (
                      <Box className={clsx(styles.noticeBanner, styles.noticeBannerWarning)}>
                        {clarificationRequestMeta && (
                          <Typography color='textSecondary' gutterBottom variant='caption'>
                            {t(messages.sections.clarification_history.requested_at, {
                              date: formatTimestamp(clarificationRequestMeta.date),
                              name: clarificationRequestMeta.name ?? '—',
                            })}
                          </Typography>
                        )}
                        <Typography className={styles.preLineValue} variant='body2'>
                          {detail.administratorClarificationComment}
                        </Typography>
                      </Box>
                    )}
                    {detail.ownerResponseComment && (
                      <>
                        <div className={styles.fieldLabel}>{t(messages.sections.clarification_history.owner_response)}</div>
                        <Typography className={styles.preLineValue} variant='body2'>
                          {detail.ownerResponseComment}
                        </Typography>
                      </>
                    )}
                  </AdminDpiaReviewSection>
                )}

                {reviewSummary && (
                  <AdminDpiaReviewSection
                    eyebrow={t(messages.sections.summary.eyebrow)}
                    help={t(messages.sections.summary.help)}
                    title={t(messages.sections.summary.title)}
                  >
                    <div className={styles.reviewSummaryMetricsRow}>
                      <Box className={styles.metricTile}>
                        <Box className={styles.metricCard}>
                          <div className={styles.metricLabel}>{t(messages.sections.summary.documented_risks)}</div>
                          <div className={styles.metricValue}>{reviewSummary.documentedRisks}</div>
                        </Box>
                      </Box>
                      <Box className={styles.metricTile}>
                        <Box className={styles.metricCard}>
                          <div className={styles.metricLabel}>{t(messages.sections.summary.high_risks)}</div>
                          <div className={styles.metricValue}>{reviewSummary.highOrVeryHighRisks}</div>
                        </Box>
                      </Box>
                      <Box className={styles.metricTile}>
                        <Box className={styles.metricCard}>
                          <div className={styles.metricLabel}>{t(messages.sections.summary.open_measures)}</div>
                          <div className={styles.metricValue}>{reviewSummary.plannedOrOngoingMeasures}</div>
                        </Box>
                      </Box>
                      <Box className={styles.metricTile}>
                        <Box className={styles.metricCard}>
                          <div className={styles.metricLabel}>{t(messages.sections.summary.residual_risk)}</div>
                          <div className={styles.metricValue}>{t(ownerMessages.residual_risk[reviewSummary.residualRiskLevel])}</div>
                        </Box>
                      </Box>
                    </div>
                    {reviewWarnings.map(warning => (
                      <Box
                        key={warning.id}
                        className={clsx(styles.noticeBanner, warning.blocking ? styles.noticeBannerDanger : styles.noticeBannerWarning)}
                      >
                        <Typography variant='body2'>{warning.message}</Typography>
                      </Box>
                    ))}
                  </AdminDpiaReviewSection>
                )}
              </Box>
            )}

            <AdminDpiaReviewSection
              className={styles.adminDpiaReviewDecisionSection}
              contentClassName={styles.apaSectionFields}
              eyebrow={t(messages.sections.decision.eyebrow)}
              help={t(messages.sections.decision.help)}
              title={t(messages.sections.decision.title)}
            >
              {detail.status === 'decision_made' && (
                <>
                  {detail.decision && (
                    <Typography className={styles.apaSectionNote} variant='body2'>
                      <strong>{getDpiaDecisionLabel(detail.decision, t)}</strong>
                    </Typography>
                  )}
                  <Typography className={styles.apaSectionNote} variant='body2'>
                    {t(messages.sections.decision.decision_made_notice, {
                      date: formatTimestamp(detail.approvedAt),
                      name: detail.approvedByName ?? '—',
                    })}
                  </Typography>
                </>
              )}

              {detail.status === 'clarification_requested' && (
                <Typography className={styles.apaSectionNote} variant='body2'>
                  {t(messages.sections.decision.awaiting_resubmission)}
                </Typography>
              )}

              {canDecide && (
                <>
                  {validationErrors.approval && (
                    <Typography color='error' variant='body2'>
                      {validationErrors.approval}
                    </Typography>
                  )}

                  {blockingErrors.length > 0 && (
                    <Box className={clsx(styles.noticeBanner, styles.noticeBannerDanger)}>
                      {blockingErrors.map(error => (
                        <Typography key={error} color='error' variant='body2'>
                          {error}
                        </Typography>
                      ))}
                    </Box>
                  )}

                  <FormControl component='fieldset' error={Boolean(validationErrors.decision)} fullWidth>
                    <RadioGroup
                      aria-label={t(messages.sections.decision.title)}
                      className={styles.reviewDecisionOptions}
                      onChange={event => {
                        setDpiaDecision(event.target.value as DpiaDecision);
                        setValidationErrors({});
                      }}
                      value={dpiaDecision ?? ''}
                    >
                      {DPIA_DECISION_OPTIONS.map(option => {
                        const label = getDpiaDecisionLabel(option, t);
                        const tooltip = getDpiaDecisionTooltip(option, t);

                        return (
                          <FormControlLabel
                            key={option}
                            control={<Radio color='primary' />}
                            label={
                              <Tooltip title={tooltip}>
                                <span>{label}</span>
                              </Tooltip>
                            }
                            value={option}
                          />
                        );
                      })}
                    </RadioGroup>
                    {validationErrors.decision && <FormHelperText>{validationErrors.decision}</FormHelperText>}
                  </FormControl>

                  <TextField
                    className={styles.reviewDecisionComment}
                    error={Boolean(validationErrors.reviewComment)}
                    fullWidth
                    helperText={validationErrors.reviewComment || t(messages.sections.decision.review_comment_help)}
                    label={t(messages.sections.decision.review_comment)}
                    margin='none'
                    multiline
                    onChange={event => {
                      setDecisionComment(event.target.value);
                      setValidationErrors({});
                    }}
                    rows={4}
                    value={decisionComment}
                    variant='outlined'
                  />

                  {canShowSubmitDecision && (
                    <div className={styles.reviewDecisionSubmit}>
                      <ColoredButton
                        className=''
                        customColor='secondary'
                        disabled={isSubmitDecisionDisabled}
                        icon={false}
                        onClick={handleSubmitDecision}
                        showLoader={isApproving}
                        textLike={false}
                        variant='contained'
                      >
                        {t(messages.actions.submit_decision)}
                      </ColoredButton>
                    </div>
                  )}

                  <Box className={styles.apaSectionFields} style={{ marginTop: 24 }}>
                    <Typography className={styles.apaSectionTitle} variant='h4'>
                      {t(messages.sections.decision.clarification_title)}
                    </Typography>
                    <TextField
                      className={styles.reviewDecisionComment}
                      error={Boolean(validationErrors.clarificationComment)}
                      fullWidth
                      helperText={
                        validationErrors.clarificationComment || t(processing_activities_messages.asset_ropa.modals.clarification.help)
                      }
                      label={t(processing_activities_messages.asset_ropa.modals.clarification.comment)}
                      margin='none'
                      multiline
                      onChange={event => {
                        setClarificationComment(event.target.value);
                        setValidationErrors({});
                      }}
                      rows={4}
                      value={clarificationComment}
                      variant='outlined'
                    />
                    <div className={styles.reviewDecisionSubmit}>
                      <ColoredButton
                        className=''
                        customColor='secondary'
                        disabled={isRequestingClarification || !clarificationComment.trim()}
                        icon={false}
                        onClick={handleSubmitClarification}
                        showLoader={isRequestingClarification}
                        textLike={false}
                        variant='outlined'
                      >
                        {t(messages.actions.request_clarification)}
                      </ColoredButton>
                    </div>
                  </Box>
                </>
              )}

              {!canDecide && detail.reviewDecisionComment && (
                <TextField
                  disabled
                  fullWidth
                  label={t(messages.sections.decision.review_comment)}
                  margin='none'
                  multiline
                  rows={4}
                  value={detail.reviewDecisionComment}
                  variant='outlined'
                />
              )}
            </AdminDpiaReviewSection>
          </section>
        </div>
      </>
    );
  }

  return (
    <ErrorBoundary>
      <Container maxWidth='lg'>
        <ManagePageHeader backButton noBorder onBack={navigateBack} subtitle={t(messages.page_subtitle)}>
          {detail?.title ?? t(messages.page_title)}
        </ManagePageHeader>
        {content}
      </Container>
    </ErrorBoundary>
  );
};

export default AdminDpiaReviewPage;
